import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { attachmentPartSchema } from './chat';
import {
  CARE_CHAT_ATTACHMENT_ALLOWED_MEDIA_TYPES,
  CARE_CHAT_ATTACHMENT_MAX_SIZE_BYTES,
} from './care-chat-attachment';
import {
  conversationReadWatermarkPositionSchema,
  atlasConversationParticipantSchema,
  chatConversationSchema,
  memberConversationParticipantSchema,
  conversationMessageSchema,
  conversationReadStateSchema,
} from './chat-response';

/**
 * Wire contract shared by the member and staff care-chat surfaces.
 *
 * Every shape here COMPOSES the audience-safe projections in `chat-response.ts`
 * rather than restating their fields, so the projections stay the single place a
 * column reaches the wire (and the single place a drift guard has to watch).
 *
 * Two properties this contract carries deliberately:
 *
 *   - Paging is an exclusive position keyset in both directions. Positions are
 *     monotonic but MAY contain gaps, so a client cursors with `position < before`
 *     / `position > after` and never infers loss from non-contiguity. `hasOlder`
 *     answers "is there another page behind this one" without a count query.
 *   - A send is idempotent on the caller-supplied `clientMessageId`, and the
 *     acknowledgement returns the whole committed message (not just its id) so an
 *     optimistic client reconciles against the canonical row — including the
 *     server-assigned `position`, which is the only ordering authority.
 *
 * A client may author exactly one text or attachment part. Other stored content
 * kinds (`genui`, `tool_use`, and `tool_result`) remain rejected structurally at
 * the boundary rather than by a later check.
 */

/** Page size when the caller names none. */
export const CARE_CHAT_PAGE_SIZE_DEFAULT = 50;
/** Hard ceiling on a single history page. */
export const CARE_CHAT_PAGE_SIZE_MAX = 100;
/** Upper bound on one user-authored message body. */
export const CARE_CHAT_TEXT_PART_MAX_LENGTH = 4000;
/** Upper bound on a caller-supplied idempotency key. */
export const CARE_CHAT_CLIENT_MESSAGE_ID_MAX_LENGTH = 200;

// ─── Conversation resolution ──────────────────────────────────────────────────

// Two audience contracts, deliberately split: the member surface is the
// compatibility boundary (shipped clients parse participants strictly), so its
// schema must REJECT internal-only fields rather than tolerate them. The
// internal surface alone documents and accepts `roleClass`.
export const careChatConversationDataSchema = z
  .object({
    conversation: chatConversationSchema,
    participants: z.array(memberConversationParticipantSchema),
    readState: conversationReadStateSchema.nullable(),
  })
  .strict();
export type CareChatConversationData = z.infer<typeof careChatConversationDataSchema>;
export const careChatConversationResponseSchema = apiEnvelope(careChatConversationDataSchema);
export type CareChatConversationResponse = z.infer<typeof careChatConversationResponseSchema>;

export const internalCareChatConversationDataSchema = z
  .object({
    conversation: chatConversationSchema,
    participants: z.array(atlasConversationParticipantSchema),
    readState: conversationReadStateSchema.nullable(),
  })
  .strict();
export type InternalCareChatConversationData = z.infer<
  typeof internalCareChatConversationDataSchema
>;
export const internalCareChatConversationResponseSchema = apiEnvelope(
  internalCareChatConversationDataSchema,
);
export type InternalCareChatConversationResponse = z.infer<
  typeof internalCareChatConversationResponseSchema
>;

// ─── History page ─────────────────────────────────────────────────────────────

export const careChatMessagePageDataSchema = z
  .object({
    items: z.array(conversationMessageSchema),
    hasOlder: z.boolean(),
  })
  .strict();
export type CareChatMessagePageData = z.infer<typeof careChatMessagePageDataSchema>;
export const careChatMessagePageResponseSchema = apiEnvelope(careChatMessagePageDataSchema);
export type CareChatMessagePageResponse = z.infer<typeof careChatMessagePageResponseSchema>;

// ─── Send ─────────────────────────────────────────────────────────────────────

/**
 * The text part a care-chat client may author. Kept as its own strict object
 * instead of reusing the stored `textBlockSchema` so a later widening of the
 * stored union cannot silently widen what this surface accepts, and so the
 * length bound lives with the input rather than with storage.
 */
export const careChatTextPartSchema = z
  .object({
    type: z.literal('text'),
    text: z.string().min(1).max(CARE_CHAT_TEXT_PART_MAX_LENGTH),
  })
  .strict();
export type CareChatTextPart = z.infer<typeof careChatTextPartSchema>;

/** @deprecated Use the actor-neutral careChatTextPartSchema. */
export const careChatStaffTextPartSchema = careChatTextPartSchema;
/** @deprecated Use the actor-neutral CareChatTextPart. */
export type CareChatStaffTextPart = CareChatTextPart;

export const careChatSendPartSchema = z.union([careChatTextPartSchema, attachmentPartSchema]);
export type CareChatSendPart = z.infer<typeof careChatSendPartSchema>;

export const careChatSendRequestSchema = z
  .object({
    clientMessageId: z.string().min(1).max(CARE_CHAT_CLIENT_MESSAGE_ID_MAX_LENGTH),
    parts: z.array(careChatSendPartSchema).min(1).max(1),
  })
  .strict();
export type CareChatSendRequest = z.infer<typeof careChatSendRequestSchema>;

export const careChatSendDataSchema = z
  .object({
    message: conversationMessageSchema,
  })
  .strict();
export type CareChatSendData = z.infer<typeof careChatSendDataSchema>;
export const careChatSendResponseSchema = apiEnvelope(careChatSendDataSchema);
export type CareChatSendResponse = z.infer<typeof careChatSendResponseSchema>;

// ─── Read state ───────────────────────────────────────────────────────────────

export const careChatReadRequestSchema = z
  .object({
    lastReadPosition: conversationReadWatermarkPositionSchema,
  })
  .strict();
export type CareChatReadRequest = z.infer<typeof careChatReadRequestSchema>;

export const careChatReadDataSchema = z
  .object({
    readState: conversationReadStateSchema,
  })
  .strict();
export type CareChatReadData = z.infer<typeof careChatReadDataSchema>;
export const careChatReadResponseSchema = apiEnvelope(careChatReadDataSchema);
export type CareChatReadResponse = z.infer<typeof careChatReadResponseSchema>;

// ─── Attachment upload ────────────────────────────────────────────────────────

export {
  CARE_CHAT_ATTACHMENT_ALLOWED_MEDIA_TYPES,
  CARE_CHAT_ATTACHMENT_MAX_SIZE_BYTES,
  isAllowedCareChatAttachmentMediaType,
  type CareChatAttachmentMediaType,
} from './care-chat-attachment';

export const careChatAttachmentUploadRequestSchema = z
  .object({
    mediaType: z.enum(CARE_CHAT_ATTACHMENT_ALLOWED_MEDIA_TYPES),
    sizeBytes: z.number().int().positive().max(CARE_CHAT_ATTACHMENT_MAX_SIZE_BYTES),
    fileName: z.string().max(255).optional(),
  })
  .strict();
export type CareChatAttachmentUploadRequest = z.infer<typeof careChatAttachmentUploadRequestSchema>;

export const careChatAttachmentUploadDataSchema = z
  .object({
    attachmentId: z.string().uuid(),
    uploadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
  })
  .strict();
export type CareChatAttachmentUploadData = z.infer<typeof careChatAttachmentUploadDataSchema>;
export const careChatAttachmentUploadResponseSchema = apiEnvelope(
  careChatAttachmentUploadDataSchema,
);
export type CareChatAttachmentUploadResponse = z.infer<
  typeof careChatAttachmentUploadResponseSchema
>;

export const careChatAttachmentDownloadUrlDataSchema = z
  .object({
    downloadUrl: z.string().url(),
    expiresAt: z.string().datetime(),
  })
  .strict();
export type CareChatAttachmentDownloadUrlData = z.infer<
  typeof careChatAttachmentDownloadUrlDataSchema
>;
export const careChatAttachmentDownloadUrlResponseSchema = apiEnvelope(
  careChatAttachmentDownloadUrlDataSchema,
);
export type CareChatAttachmentDownloadUrlResponse = z.infer<
  typeof careChatAttachmentDownloadUrlResponseSchema
>;

export const careChatAttachmentCommitDataSchema = z.object({ ok: z.literal(true) }).strict();
export const careChatAttachmentCommitResponseSchema = apiEnvelope(
  careChatAttachmentCommitDataSchema,
);
export type CareChatAttachmentCommitResponse = z.infer<
  typeof careChatAttachmentCommitResponseSchema
>;

/** Read-only navigation count; never provisions a conversation. */
export const careChatUnreadDataSchema = z
  .object({ unreadCount: z.number().int().nonnegative() })
  .strict();
export const careChatUnreadResponseSchema = apiEnvelope(careChatUnreadDataSchema);
